"use client"

import { useState, useEffect } from "react"
import styled from "styled-components"
import { Users, X, UserPlus, UserCheck } from "lucide-react"
import { useAuth } from "../context/AuthContext"
import { useTheme } from "../context/ThemeContext"
import { useNavigate } from "react-router-dom"

const Container = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 12px;
  box-shadow: ${props => props.theme.shadow};
  padding: 20px;
  margin-bottom: 20px;
  transition: all 0.3s ease;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`

const Title = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: ${props => props.theme.textPrimary};
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.textMuted};
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
  
  &:hover {
    color: ${props => props.theme.textSecondary};
    background-color: ${props => props.theme.hoverBackground};
  }
`

const UsersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const UserCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${props => props.theme.hoverBackground};
  }
`

const UserAvatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background-color: ${props => props.theme.borderLight};
  background-image: ${props => props.$avatar ? `url(${props.$avatar})` : 'none'};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.textMuted};
  cursor: pointer;
  overflow: hidden;
`

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
  cursor: pointer;
`

const UserName = styled.div`
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const UserUsername = styled.div`
  font-size: 14px;
  color: ${props => props.theme.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const FollowButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  border: none;
  border-radius: 6px;
  background-color: ${props => props.$isFollowing ? props.theme.buttonSecondary : props.theme.buttonPrimary};
  color: ${props => props.$isFollowing ? props.theme.textSecondary : 'white'};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  
  &:hover {
    background-color: ${props => props.$isFollowing ? props.theme.buttonSecondaryHover : props.theme.buttonPrimaryHover};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const LoadingState = styled.div`
  text-align: center;
  color: ${props => props.theme.textSecondary};
  font-size: 14px;
  padding: 20px;
`

const SeeAllButton = styled.button`
  width: 100%;
  padding: 8px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background-color: ${props => props.theme.cardBackground};
  color: ${props => props.theme.textSecondary};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  margin-top: 8px;
  transition: all 0.2s;

  &:hover {
    background-color: ${props => props.theme.hoverBackground};
    border-color: ${props => props.theme.borderLight};
  }
`

// === 가로 슬라이드(인스타 스타일) 컴포넌트들 ===
const CarouselTrack = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: thin;
  padding-bottom: 4px;
  margin: 0 -4px;
  padding-left: 4px;
  padding-right: 4px;

  &::-webkit-scrollbar { height: 6px; }
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.border};
    border-radius: 3px;
  }
`

const CarouselCard = styled.div`
  flex: 0 0 160px;
  scroll-snap-align: start;
  background-color: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 12px;
  padding: 14px 12px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  transition: border-color 0.15s, transform 0.15s;

  &:hover {
    border-color: ${props => props.theme.borderLight};
    transform: translateY(-1px);
  }
`

const CarouselDismiss = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  background: none;
  border: none;
  color: ${props => props.theme.textMuted};
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  display: flex;

  &:hover {
    background-color: ${props => props.theme.hoverBackground};
    color: ${props => props.theme.textSecondary};
  }
`

const CarouselAvatar = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background-color: ${props => props.theme.borderLight};
  background-image: ${props => props.$avatar ? `url(${props.$avatar})` : 'none'};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.textMuted};
  cursor: pointer;
  margin-bottom: 10px;
  border: 2px solid ${props => props.theme.cardBackground};
  box-shadow: 0 0 0 1px ${props => props.theme.border};
`

const CarouselName = styled.div`
  font-weight: 600;
  font-size: 13px;
  color: ${props => props.theme.textPrimary};
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
`

const CarouselReason = styled.div`
  font-size: 11px;
  color: ${props => props.theme.textSecondary};
  margin: 4px 0 10px;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const CarouselFollowButton = styled.button`
  width: 100%;
  padding: 7px 10px;
  border: none;
  border-radius: 6px;
  background-color: ${props => props.$isFollowing ? props.theme.buttonSecondary : props.theme.buttonPrimary};
  color: ${props => props.$isFollowing ? props.theme.textSecondary : 'white'};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;

  &:hover {
    background-color: ${props => props.$isFollowing ? props.theme.buttonSecondaryHover : props.theme.buttonPrimaryHover};
  }

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

export default function PeopleYouMayKnow({ limit = 3, onClose, variant = "list" }) {
  const { isLoggedIn, user } = useAuth()
  const { theme } = useTheme()
  const navigate = useNavigate()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [followingStates, setFollowingStates] = useState({})
  const [followLoading, setFollowLoading] = useState({})

  const getAuthHeaders = () => {
    const username = user?.username || user?.id
    return username ? { 'X-Username': String(username) } : {}
  }
  
  useEffect(() => {
    if (isLoggedIn) {
      fetchRandomUsers()
    }
  }, [isLoggedIn])
  
  const fetchRandomUsers = async () => {
    setLoading(true)
    try {
      const response = await fetch(`/user/recommended?limit=${limit}`, {
        credentials: 'include'
      })
      
      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          setUsers(data.users)
          // 각 사용자의 팔로우 상태 확인
          checkFollowStates(data.users)
        }
      } else {
        // API 호출 실패 시 더미 데이터로 테스트
        console.log('API 호출 실패, 더미 데이터 사용')
        const dummyUsers = [
          {
            id: 1,
            username: 'john_doe',
            full_name: 'John Doe',
            avatar_url: null,
            post_count: 15,
            follower_count: 120
          },
          {
            id: 2,
            username: 'jane_smith',
            full_name: 'Jane Smith',
            avatar_url: null,
            post_count: 8,
            follower_count: 89
          },
          {
            id: 3,
            username: 'coding_master',
            full_name: '코딩 마스터',
            avatar_url: null,
            post_count: 25,
            follower_count: 200
          }
        ]
        setUsers(dummyUsers)
        checkFollowStates(dummyUsers)
      }
    } catch (error) {
      console.error('추천 사용자 로드 오류:', error)
      // 에러 발생 시에도 더미 데이터 표시
      const dummyUsers = [
        {
          id: 1,
          username: 'test_user',
          full_name: '테스트 유저',
          avatar_url: null,
          bio: '테스트 계정입니다',
          post_count: 5,
          follower_count: 10
        }
      ]
      setUsers(dummyUsers)
      checkFollowStates(dummyUsers)
    } finally {
      setLoading(false)
    }
  }
  
  const checkFollowStates = async (usersList) => {
    const states = {}
    for (const u of usersList) {
      try {
        const response = await fetch(`/user/follow-status/${u.id}`, {
          credentials: 'include',
          headers: getAuthHeaders()
        })
        if (response.ok) {
          const data = await response.json()
          states[u.id] = data.isFollowing
        }
      } catch (error) {
        console.error(`팔로우 상태 확인 오류 (${u.id}):`, error)
        states[u.id] = false
      }
    }
    setFollowingStates(states)
  }
  
  const handleFollow = async (userId) => {
    setFollowLoading(prev => ({ ...prev, [userId]: true }))
    try {
      const response = await fetch(`/user/follow/${userId}`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders()
      })
      
      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          setFollowingStates(prev => ({
            ...prev,
            [userId]: data.isFollowing
          }))
        }
      }
    } catch (error) {
      console.error('팔로우 오류:', error)
    } finally {
      setFollowLoading(prev => ({ ...prev, [userId]: false }))
    }
  }
  
  const handleUserClick = (username) => {
    navigate(`/profile/${username}`)
  }
  
  // 디버그 로그
  console.log('PeopleYouMayKnow - isLoggedIn:', isLoggedIn, 'users:', users)
  
  // 로그인하지 않은 경우 표시하지 않음
  if (!isLoggedIn) {
    console.log('PeopleYouMayKnow - 로그인하지 않음, 렌더링 안함')
    return null
  }
  
  if (loading) {
    return (
      <Container theme={theme}>
        <Header>
          <Title theme={theme}>회원님을 위한 추천</Title>
          {onClose && (
            <CloseButton theme={theme} onClick={onClose}>
              <X size={16} />
            </CloseButton>
          )}
        </Header>
        <LoadingState theme={theme}>추천 사용자를 불러오는 중...</LoadingState>
      </Container>
    )
  }
  
  if (users.length === 0) {
    return null
  }

  const handleDismiss = (userId) => {
    setUsers(prev => prev.filter(u => u.id !== userId))
  }

  // 추천 사유 텍스트 (mutual_count → bio → username 폴백)
  const getReason = (u) => {
    if (u.mutual_count && u.mutual_count > 0) return `함께 아는 ${u.mutual_count}명`
    if (u.mutual_followers_count && u.mutual_followers_count > 0) return `함께 아는 ${u.mutual_followers_count}명`
    if (u.follower_count > 0) return `팔로워 ${u.follower_count}명`
    return `@${u.username}`
  }

  // 가로 슬라이드 (인스타 스타일)
  if (variant === "carousel") {
    return (
      <Container theme={theme}>
        <Header>
          <Title theme={theme}>알 수도 있는 사람들</Title>
          {onClose && (
            <CloseButton theme={theme} onClick={onClose}>
              <X size={16} />
            </CloseButton>
          )}
        </Header>

        <CarouselTrack theme={theme}>
          {users.map((u) => (
            <CarouselCard key={u.id} theme={theme}>
              <CarouselDismiss theme={theme} onClick={() => handleDismiss(u.id)} aria-label="dismiss">
                <X size={14} />
              </CarouselDismiss>
              <CarouselAvatar
                theme={theme}
                $avatar={u.avatar_url}
                onClick={() => handleUserClick(u.username)}
              >
                {!u.avatar_url && <Users size={28} />}
              </CarouselAvatar>
              <CarouselName theme={theme} onClick={() => handleUserClick(u.username)}>
                {u.full_name || u.username}
              </CarouselName>
              <CarouselReason theme={theme}>{getReason(u)}</CarouselReason>
              <CarouselFollowButton
                theme={theme}
                $isFollowing={followingStates[u.id]}
                disabled={followLoading[u.id]}
                onClick={() => handleFollow(u.id)}
              >
                {followLoading[u.id] ? '...' : followingStates[u.id] ? (
                  <><UserCheck size={12} />팔로잉</>
                ) : (
                  <><UserPlus size={12} />팔로우</>
                )}
              </CarouselFollowButton>
            </CarouselCard>
          ))}
        </CarouselTrack>
      </Container>
    )
  }

  // 기존 세로 리스트
  return (
    <Container theme={theme}>
      <Header>
        <Title theme={theme}>회원님을 위한 추천</Title>
        {onClose && (
          <CloseButton theme={theme} onClick={onClose}>
            <X size={16} />
          </CloseButton>
        )}
      </Header>

      <UsersList>
        {users.map((user) => (
          <UserCard key={user.id} theme={theme}>
            <UserAvatar
              theme={theme}
              $avatar={user.avatar_url}
              onClick={() => handleUserClick(user.username)}
            >
              {!user.avatar_url && <Users size={20} />}
            </UserAvatar>

            <UserInfo onClick={() => handleUserClick(user.username)}>
              <UserName theme={theme}>{user.full_name || user.username}</UserName>
              <UserUsername theme={theme}>@{user.username}</UserUsername>
            </UserInfo>

            <FollowButton
              theme={theme}
              $isFollowing={followingStates[user.id]}
              disabled={followLoading[user.id]}
              onClick={() => handleFollow(user.id)}
            >
              {followLoading[user.id] ? (
                '처리중...'
              ) : followingStates[user.id] ? (
                <>
                  <UserCheck size={14} />
                  팔로잉
                </>
              ) : (
                <>
                  <UserPlus size={14} />
                  팔로우
                </>
              )}
            </FollowButton>
          </UserCard>
        ))}
      </UsersList>

      <SeeAllButton theme={theme} onClick={() => navigate('/people')}>
        모두 보기
      </SeeAllButton>
    </Container>
  )
}